import { useMemo } from "react";
import { calcNetProfit, useApp } from "@/context/AppContext";

export interface RecommendationSummary {
  count: number;
  highCount: number;
}

export function useRecommendationSummary(): RecommendationSummary {
  const { cards, sales } = useApp();

  return useMemo(() => {
    let count = 0;
    let highCount = 0;

    const activeCards = cards.filter(
      (c) => c.listingStatus !== "Sold" && c.quantity > 0,
    );
    const unlistedCards = activeCards.filter(
      (c) => c.listingStatus === "Unlisted",
    );
    const soldCards = cards.filter((c) => c.listingStatus === "Sold");

    const salesByCardId = new Map<string, typeof sales>();
    sales.forEach((sale) => {
      if (sale.itemType === "Card") {
        const arr = salesByCardId.get(sale.itemId) ?? [];
        arr.push(sale);
        salesByCardId.set(sale.itemId, arr);
      }
    });

    const profitableUnlisted = unlistedCards.filter(
      (c) => (c.marketPrice - c.costBasis) * c.quantity > 0,
    );
    if (profitableUnlisted.length > 0) {
      const totalGap = profitableUnlisted.reduce(
        (s, c) => s + (c.marketPrice - c.costBasis) * c.quantity,
        0,
      );
      count++;
      if (totalGap >= 50) highCount++;
    }

    const underwater = activeCards.filter(
      (c) =>
        c.costBasis > 0 && c.marketPrice > 0 && c.marketPrice < c.costBasis,
    );
    if (underwater.length > 0) {
      count++;
      highCount++;
    }

    if (soldCards.length > 0) {
      type SetStat = { totalROI: number; count: number };
      const setStats = new Map<string, SetStat>();
      soldCards.forEach((card) => {
        (salesByCardId.get(card.id) ?? []).forEach((sale) => {
          const net = calcNetProfit(sale);
          const roi =
            card.costBasis > 0
              ? ((net - card.costBasis) / card.costBasis) * 100
              : 0;
          const prev = setStats.get(card.setName) ?? { totalROI: 0, count: 0 };
          setStats.set(card.setName, {
            totalROI: prev.totalROI + roi,
            count: prev.count + 1,
          });
        });
      });
      const hasTopSet = Array.from(setStats.values()).some((s) => s.count >= 1);
      if (hasTopSet) count++;
    }

    return { count, highCount };
  }, [cards, sales]);
}
